<template>
	<ext-dialog
		v-model="value"
		:title="dialogTitle"
		:primaryButtonText="$t('save')"
		@primaryButtonAction="save"
		@closed="close"
	>
		<template #body>
			<v-form ref="form" v-model="valid">
				<v-text-field v-model="batch.measurement_no" :label="$t('batch_no')" :rules="rules.measurement_no" required />
				<v-select
					v-model="batch.quantityStatus"
					:items="quantityStatusOptions"
					:label="$t('stock_status')"
					append-icon="icon-angle-down"
					hide-details
					outlined
					required
					:menu-props="{ offsetY: true }"
					class="py-2"
				/>
				<v-file-input
					v-model="selectedFiles"
					:label="$t('images')"
					multiple
					accept="image/*"
					show-size
					hide-details
					outlined
					prepend-icon=""
					@change="updateNewImagePreviews"
					class="py-2"
				/>
				<div class="d-flex align-center py-1">
					<span class="subtitle-1">{{ $t("measurements") }} ({{ unitCode }})</span>
					<v-spacer />
					<v-btn icon color="primary" @click="addMeasurement">
						<v-icon>icon-plus</v-icon>
					</v-btn>
				</div>
				<v-divider class="mb-4" />
				<template v-if="selectedFormat.requiredMeasurements.length === 0">
					<div v-for="(m, i) in batch.measurements" :key="i" class="my-2">
						<v-row dense>
							<v-col cols="4" class="d-flex flex-column">
								<v-text-field
									v-model.number="m.value"
									:label="unitCode"
									type="number"
									:disabled="isFixed('value')"
									required
								/>
								<v-text-field
									v-if="hasGross"
									class="mt-2"
									v-model.number="m.grossValue"
									:label="grossLabel('value')"
									type="number"
								/>
							</v-col>

							<v-col cols="2" v-if="dimUnit">
								<v-text-field :value="dimUnit" :label="$t('unit')" disabled hide-details />
							</v-col>
							<v-col cols="3">
								<v-text-field
									v-model.number="m.pieces"
									:label="$t('pieces')"
									type="number"
									:rules="[measurementRule('pieces')]"
									required
								/>
							</v-col>
							<v-col cols="1" class="d-flex align-center justify-end">
								<v-btn icon @click="removeMeasurement(i)"><v-icon>icon-delete</v-icon></v-btn>
							</v-col>
						</v-row>
					</div>
				</template>
				<template v-else>
					<div v-for="(m, i) in batch.measurements" :key="i" class="my-2">
						<v-row dense>
							<v-col v-for="f in dimFields" :key="`n-${f}`" :cols="inputColWidth" class="d-flex flex-column">
								<v-text-field
									v-model.number="m[f]"
									:label="$t(f)"
									type="number"
									:disabled="isFixed(f)"
									:rules="[measurementRule(f)]"
									required
								/>
								<v-text-field
									v-if="hasGross"
									class="mt-2"
									v-model.number="m[grossProp(f)]"
									:label="grossLabel(f)"
									type="number"
								/>
							</v-col>

							<v-col :cols="unitColWidth" v-if="dimUnit">
								<v-text-field :value="dimUnit" :label="$t('unit')" disabled hide-details />
							</v-col>
							<v-col :cols="pieceColWidth">
								<v-text-field
									v-model.number="m.pieces"
									:label="$t('pieces')"
									type="number"
									:rules="[measurementRule('pieces')]"
									required
								/>
							</v-col>
							<v-col cols="1" class="d-flex align-center justify-end">
								<v-btn icon @click="removeMeasurement(i)"><v-icon>icon-delete</v-icon></v-btn>
							</v-col>
						</v-row>
					</div>
				</template>
				<v-text-field
					v-model.number="batch.quantity"
					:label="$t('quantity')"
					type="number"
					:rules="[measurementRule('quantity')]"
					:append-outer-text="unitCode"
					required
				/>
				<v-text-field v-model.number="batch.weight" :label="$t('weight_kg')" type="number" />
			</v-form>
			<v-text-field v-model="batch.notes" :label="$t('notes')" class="py-2" hide-details />
			<v-checkbox
				v-model="batch.visibility"
				:label="$t('show_in_stocks')"
				on-icon="icon-checkbox-checked"
				off-icon="icon-checkbox-unchecked"
				hide-details
			/>
		</template>
	</ext-dialog>
</template>

<script>
import ExtDialog from "../../../partials/dialogs/ExtDialog.vue";
import remote from "../../../../data/remote";
import { stockStatus } from "../../../../utils/enum";
import { LIN_SCALE } from "../../../mixins/measure.mixin.js";

export default {
	name: "NewBatchDialog",
	components: { ExtDialog },
	model: { prop: "value", event: "input" },
	props: {
		value: Boolean,
		stockId: String,
		companyId: String,
		unitCode: String,
		formatId: [String, Object],
		measurementId: [String, Object],
		formatMeta: Object,
		measurementMeta: Object,
		batchData: Object,
		autoComputedTotal: Boolean,
	},
	data() {
		return {
			valid: false,
			batch: { measurement_no: "", measurements: [{ value: null, pieces: null, measurementId: null }] },
			selectedFiles: [],
			existingImages: [],
			existingImagePreviews: [],
			newImagePreviews: [],
			showErrors: false,
			fixedDims: {},
			fixedUnit: "",
			rules: {
				measurement_no: [
					(v) =>
						!this.showErrors ||
						(v && v.toString().trim() !== "") ||
						this.$t("$validation.required", { item: this.$t("batch_no") }),
				],
				value: [(v) => !this.showErrors || v !== null || this.$t("$validation.required", { item: this.$t("value") })],
				pieces: [
					(v) =>
						!this.showErrors ||
						(v !== null && Number(v) > 0) ||
						this.$t("$validation.required", { item: this.$t("pieces") }),
				],
				quantity: [
					(v) => !this.showErrors || v !== null || this.$t("$validation.required", { item: this.$t("quantity") }),
				],
			},
		};
	},
	computed: {
		selectedFormat() {
			return this.formatMeta || { requiredMeasurements: [], conversionFormula: "value" };
		},
		hasGross() {
			return Boolean(this.measurementMeta?.hasGross);
		},
		dimFields() {
			return this.selectedFormat.requiredMeasurements;
		},
		inputColWidth() {
			const n = this.dimFields.length || 1;
			return Math.floor(8 / n);
		},
		unitColWidth() {
			return 1;
		},
		pieceColWidth() {
			return 2;
		},
		dialogTitle() {
			return this.batch.id ? this.$t("edit_stock_batch") : this.$t("add_stock_batch");
		},
		quantityStatusOptions() {
			return Object.values(stockStatus).map((s) => ({ text: this.$t(s), value: s }));
		},
		dimUnit() {
			return this.fixedUnit;
		},
	},
	watch: {
		value(v) {
			if (v) this.initData();
		},
		batch: {
			handler() {
				this.recomputeQuantity();
			},
			deep: true,
		},
	},
	methods: {
		grossLabel(key) {
			const k = `gross_${key}`;
			if (this.$te && this.$te(k)) return this.$t(k);
			return `Gross ${key}`;
		},
		grossProp(f) {
			return `gross${f.charAt(0).toUpperCase()}${f.slice(1)}`;
		},
		defaultMeasurementId() {
			if (this.measurementId && this.measurementId.$oid) return this.measurementId.$oid;
			return this.measurementId?.toString?.() ?? this.measurementId;
		},
		isFixed(k) {
			return this.fixedDims[k] !== undefined && this.fixedDims[k] !== null;
		},
		measurementRule(f) {
			const label = f === "pieces" ? this.$t("pieces") : this.$t(f);
			return (v) => !this.showErrors || v !== null || this.$t("$validation.required", { item: label });
		},
		initMeasurement() {
			const required = this.selectedFormat.requiredMeasurements.length
				? this.selectedFormat.requiredMeasurements
				: ["value"];
			const m = { measurementId: this.defaultMeasurementId(), pieces: null };
			required.forEach((f) => {
				m[f] = this.fixedDims[f] !== undefined ? this.fixedDims[f] : null;
			});
			if (this.hasGross) {
				if (required.includes("length")) m.grossLength = null;
				if (required.includes("width")) m.grossWidth = null;
				if (required.includes("height")) m.grossHeight = null;
				if (required.length === 1 && required[0] === "value") m.grossValue = null;
			}
			return m;
		},
		initBatch() {
			return {
				stockId: this.stockId,
				quantity: null,
				measurements: [this.initMeasurement()],
				measurement_no: "",
				weight: null,
				images: [],
				notes: "",
				quantityStatus: "in_stock",
				visibility: true,
				companyId: this.companyId,
				formatId: this.formatId,
			};
		},
		initBatchFromProps() {
			const b = this.batchData ? JSON.parse(JSON.stringify(this.batchData)) : this.initBatch();
			if (b._id && !b.id) b.id = b._id;
			if (!Array.isArray(b.measurements) || !b.measurements.length) b.measurements = [this.initMeasurement()];
			return b;
		},
		initData() {
			this.fixedDims = {
				length: this.measurementMeta?.length ?? null,
				width: this.measurementMeta?.width ?? null,
				height: this.measurementMeta?.height ?? null,
				value: this.measurementMeta?.value ?? null,
			};
			this.fixedUnit = this.measurementMeta?.unit ?? "";
			this.showErrors = false;
			this.batch = this.initBatchFromProps();
			this.selectedFiles = [];
			this.existingImages = this.batch.images || [];
			this.clearPreviews();
			this.updateExistingImagePreviews();
			this.recomputeQuantity();
		},
		recomputeQuantity() {
			let total = 0;
			for (const m of this.batch.measurements) {
				const pcs = Number(m.pieces) || 0;
				if (!pcs) continue;
				const vars = {};
				for (const key of this.selectedFormat.requiredMeasurements) {
					const raw = Number(m[key]);
					if (!raw && raw !== 0) {
						vars[key] = null;
						continue;
					}
					const factor = LIN_SCALE[this.fixedUnit || "m"] || 1;
					vars[key] = raw * factor;
				}
				const rawValue = Number(m.value);
				if (!isNaN(rawValue)) vars.value = rawValue;
				if (Object.values(vars).some((v) => v === null)) continue;
				const formulaVars = [
					...new Set([
						...Object.keys(vars),
						...(this.selectedFormat.conversionFormula.match(/\b[a-zA-Z_]\w*\b/g) || []),
					]),
				];
				const fn = new Function(...formulaVars, `return ${this.selectedFormat.conversionFormula}`);
				const args = formulaVars.map((k) => (vars[k] !== null ? vars[k] : 0));
				const pieceArea = fn(...args);
				total += pcs * pieceArea;
			}
			if (Number.isFinite(total)) this.batch.quantity = Number(total.toFixed(4));
		},
		clearPreviews() {
			this.existingImagePreviews.forEach((u) => URL.revokeObjectURL(u));
			this.existingImagePreviews = [];
			this.newImagePreviews.forEach((u) => URL.revokeObjectURL(u));
			this.newImagePreviews = [];
		},
		addMeasurement() {
			this.batch.measurements.push(this.initMeasurement());
		},
		removeMeasurement(i) {
			if (this.batch.measurements.length > 1) this.batch.measurements.splice(i, 1);
		},
		updateExistingImagePreviews() {
			this.existingImagePreviews = [];
			return Promise.all(
				this.existingImages.map(async (k) => {
					try {
						const b = await this.loadImageBlob(k);
						this.existingImagePreviews.push(URL.createObjectURL(b));
					} catch {}
				})
			);
		},
		updateNewImagePreviews() {
			this.newImagePreviews.forEach((u) => URL.revokeObjectURL(u));
			this.newImagePreviews = this.selectedFiles.map((f) => URL.createObjectURL(f));
		},
		loadImageBlob(k) {
			return new Promise((res, rej) => remote.files.get(k, { onSuccess: (d) => res(new Blob([d])), onFail: rej }));
		},
		save() {
			this.showErrors = true;
			if (!this.$refs.form.validate()) return;
			if (this.batch.measurements.some((m) => m.pieces === null || Number(m.pieces) <= 0)) return;
			const finish = (keys) => {
				this.batch.images = [...this.existingImages, ...keys];
				this.doSave();
			};
			if (this.selectedFiles.length) {
				Promise.all(
					this.selectedFiles.map(
						(f) => new Promise((r, j) => remote.files.send(f, "batchImages", { onSuccess: (p) => r(p.key), onFail: j }))
					)
				).then(finish);
			} else finish([]);
		},
		doSave() {
			if (!this.hasGross && Array.isArray(this.batch.measurements)) {
				this.batch.measurements = this.batch.measurements.map((m) => {
					const { grossLength, grossWidth, grossHeight, grossValue, ...rest } = m;
					return rest;
				});
			}
			const cb = {
				onSuccess: () => {
					this.$emit("saved");
					this.close();
				},
				onFail: () => {},
			};
			if (this.batch.id) remote.stocks.updateBatch(this.stockId, this.batch, cb);
			else remote.stocks.addBatch(this.stockId, this.batch, cb);
		},
		close() {
			this.$emit("input", false);
			this.resetForm();
			this.$emit("cancel");
		},
		resetForm() {
			this.clearPreviews();
			this.batch = this.initBatch();
			this.existingImages = [];
			this.selectedFiles = [];
			this.showErrors = false;
		},
	},
	beforeDestroy() {
		this.clearPreviews();
	},
};
</script>

<style scoped>
.position-relative {
	position: relative;
}
.position-absolute {
	position: absolute;
}
</style>
